/**
 * Study explorer — MedDream-style dense list + column filters.
 * QIDO-RS is the backend; Tanstack-Query caches responses for 60 s.
 *
 * Issue #5 (2026-05-20) — refit on the shared DataTable: paginator,
 * sortable columns, per-user column-visibility prefs. Default sort
 * is studyDate DESC (newest first). Adds Videos / Images columns
 * sourced from POST /viewer-api/studies/counts (the QIDO proxy
 * stays a pure passthrough, so the per-SOP-class counts live on
 * their own batched endpoint).
 */
import { createMemo, createSignal, For, Show } from "solid-js";
import { createQuery } from "@tanstack/solid-query";
import { useNavigate } from "@solidjs/router";
import { listStudies, type Study } from "../api/client";
import { modalityTitle, modalityName } from "../core/modality";
import { auth } from "../auth/store";
import { modalitiesInScope, singleSpecialty } from "../auth/scope";
import { SPECIALTY_CONFIGS } from "../specialty/config";
import { can as canFn } from "../auth/types";
import { DataTable } from "../components/DataTable";
import type { DataColumn } from "../components/DataTable";
import { caseId } from "./caseId";
import { fetchStudySources, sourceLabel } from "./studySources";

interface StudyCounts {
  video_count: number;
  image_count: number;
}

async function fetchStudyCounts(uids: string[]): Promise<Record<string, StudyCounts>> {
  if (uids.length === 0) return {};
  const a = JSON.parse(sessionStorage.getItem("vpd.auth.v1") ?? "null");
  // The endpoint caps at 500 uids per call. Chunk through if the
  // list runs longer (still rare at pilot volumes).
  const out: Record<string, StudyCounts> = {};
  for (let i = 0; i < uids.length; i += 500) {
    const slice = uids.slice(i, i + 500);
    const rsp = await fetch("/viewer-api/studies/counts", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${a?.accessToken ?? ""}` },
      body: JSON.stringify({ uids: slice }),
    });
    if (!rsp.ok) continue;       // soft-fail: zero counts beat throwing the list view
    const j = await rsp.json() as Record<string, StudyCounts>;
    Object.assign(out, j);
  }
  return out;
}

export function StudyList() {
  const [q, setQ] = createSignal("");
  const [modalityFilter, setModalityFilter] = createSignal<string | null>(null);
  // Edit / delete are owner+manager+admin only.  studies.update_meta
  // covers metadata edits; studies.soft_delete covers soft-delete.
  // Both resolve to the same actor set in the matrix.  The server
  // double-enforces; we hide the buttons here for cleanliness.
  const canMutate = createMemo(() => canFn(auth(), "studies.soft_delete"));
  const [editing, setEditing]   = createSignal<Study | null>(null);
  const [deleting, setDeleting] = createSignal<Study | null>(null);
  const [busy, setBusy]         = createSignal<string | null>(null);
  const [err,  setErr]          = createSignal<string | null>(null);

  // The placeholder promises "Patient name, MRN, accession…" but
  // the server-side QIDO filter only accepts ONE key at a time and
  // different upstream implementations honour PatientName or
  // PatientID inconsistently. Simplest correct behaviour: always
  // fetch the unfiltered scope-narrowed list and do a case-
  // insensitive substring match across every identifying field
  // client-side. Scales fine up to ~10k studies (the QIDO result
  // arrives pre-paged anyway).
  const studies = createQuery(() => ({
    queryKey: ["studies"],
    queryFn: () => listStudies({}),
  }));

  const nav = useNavigate();
  // --------------------------------------------------------------
  //  Scoping — single-modality product, so the modality-code filter
  //  is constant. Per-row "own assigned" gating is enforced server-
  //  side by the assignee join.
  // --------------------------------------------------------------
  const allowedModalities = createMemo(() => modalitiesInScope(auth()));
  const soleSpec = createMemo(() => singleSpecialty(auth()));

  // Batched per-study counts. Keyed on the full UID set so a refetch
  // of the QIDO list re-runs counts in lockstep.
  const allUids = createMemo(() => (studies.data ?? []).map((s) => s.studyUid));
  const counts = createQuery(() => ({
    queryKey: ["study-counts", allUids().join(",")],
    queryFn:  () => fetchStudyCounts(allUids()),
    enabled:  allUids().length > 0,
    staleTime: 60_000,
  }));
  function countsFor(uid: string): StudyCounts {
    return counts.data?.[uid] ?? { video_count: 0, image_count: 0 };
  }
  // Which EndoEdge box uploaded each study (resolved to site name).
  const sources = createQuery(() => ({
    queryKey: ["study-sources", allUids().join(",")],
    queryFn:  () => fetchStudySources(allUids()),
    enabled:  allUids().length > 0,
    staleTime: 60_000,
  }));
  function sourceFor(uid: string): string {
    return sourceLabel(sources.data?.[uid]);
  }

  const filtered = () => {
    const m = modalityFilter();
    const rows = studies.data ?? [];
    const allowed = allowedModalities();
    const needle = q().trim().toLowerCase();
    return rows.filter((r) => {
      if (allowed.size > 0 && r.modality && !allowed.has(r.modality.toUpperCase())) return false;
      if (m && r.modality !== m) return false;
      if (needle) {
        const hay = [
          caseId(r.studyUid),
          r.patientName,
          r.patientId,
          r.accession,
          r.studyUid,
          r.description,
        ].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  };

  const modalitySet = () => {
    const s = new Set<string>();
    const allowed = allowedModalities();
    for (const r of studies.data ?? []) {
      if (!r.modality) continue;
      if (allowed.size > 0 && !allowed.has(r.modality.toUpperCase())) continue;
      s.add(r.modality);
    }
    return Array.from(s).sort();
  };

  return (
    <div class="flex h-full flex-col">
      {/* Scope banner — single-modality product, so we always show
          the modality scope to the reader. */}
      <Show when={soleSpec()}>
        <div class="flex items-center gap-2 border-b border-brand/40 bg-brand/10 px-4 py-1.5 text-[11px]">
          <span class="chip-brand !text-[9px]">
            {SPECIALTY_CONFIGS[soleSpec()!].label.toUpperCase()}
          </span>
          <span class="text-ink-900">
            Showing <span class="font-mono">{SPECIALTY_CONFIGS[soleSpec()!].label.toLowerCase()}</span> studies only.
          </span>
          <span class="ml-auto text-ink-700">
            enforced by role-based access · <code>can(action)</code>
          </span>
        </div>
      </Show>

      {/* Toolbar ---------------------------------------------------- */}
      <div class="flex flex-wrap items-center gap-3 border-b border-ink-300 bg-ink-100 px-4 py-3">
        <div class="flex items-center gap-2">
          <span class="chip-brand">Studies</span>
          <span class="text-xs text-ink-700">
            {studies.isLoading ? "loading…" : `${filtered().length} of ${studies.data?.length ?? 0}`}
          </span>
        </div>
        <div class="min-w-[240px] flex-1">
          <input
            type="search"
            placeholder="Patient name, MRN, accession…"
            class="w-full rounded border border-ink-300 bg-ink-200 px-3 py-1.5 text-sm placeholder:text-ink-700 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
            value={q()}
            onInput={(e) => setQ(e.currentTarget.value)}
          />
        </div>
        <div class="flex items-center gap-1">
          <For each={["", ...modalitySet()]}>
            {(m) => (
              <button
                type="button"
                class="rounded border border-ink-300 px-2 py-0.5 text-[11px] uppercase tracking-wider transition"
                classList={{
                  "bg-brand text-white border-brand": (modalityFilter() ?? "") === m,
                  "text-ink-700 hover:text-white": (modalityFilter() ?? "") !== m,
                }}
                title={m ? modalityTitle(m) : "Show studies of every modality"}
                aria-label={m ? modalityTitle(m) : "All modalities"}
                onClick={() => setModalityFilter(m || null)}
              >
                {m || "all"}
              </button>
            )}
          </For>
        </div>
        <button type="button" class="btn-ghost" onClick={() => studies.refetch()}>
          Refresh
        </button>
      </div>

      {/* Error banner ---------------------------------------------- */}
      <Show when={studies.isError}>
        <div class="m-4 rounded border border-danger/50 bg-danger/10 p-3 text-sm text-danger">
          QIDO call failed — {String(studies.error)}
        </div>
      </Show>

      {/* Error banner — for inline mutation errors that aren't tied
          to the QIDO list query above. */}
      <Show when={err()}>
        <div class="m-4 rounded border border-danger/50 bg-danger/10 p-3 text-[11px] text-danger">
          {err()}
        </div>
      </Show>

      {/* Table — shared DataTable: paginator, sortable headers,
          per-user column picker.  Wrapper is min-h-0 (NOT overflow-
          auto) so DataTable's inner scroll container is the one the
          sticky thead pins against. */}
      <div class="flex-1 min-h-0 p-3">
        <DataTable<Study>
          tableId="studies"
          rows={filtered}
          columns={studyColumns(canMutate, countsFor, sourceFor, setEditing, setDeleting)}
          rowKey={(s) => s.studyUid}
          onRowClick={(s) => nav(`/study/${encodeURIComponent(s.studyUid)}`)}
          defaultSort={{ col: "date", dir: "desc" }}
          defaultPageSize={50}
          loading={() => studies.isLoading}
          emptyState={<span>No studies match the current filter.</span>}
        />
      </div>

      <Show when={editing()}>
        <EditModal study={editing()!}
                   busy={busy() === "edit"}
                   onClose={() => setEditing(null)}
                   onSave={async (patch) => {
                     setBusy("edit"); setErr(null);
                     try {
                       const auth = JSON.parse(sessionStorage.getItem("vpd.auth.v1") ?? "null");
                       const rsp = await fetch(`/viewer-api/dicom-rs/studies/${encodeURIComponent(editing()!.studyUid)}`, {
                         method: "PATCH",
                         headers: { "content-type": "application/json", Authorization: `Bearer ${auth?.accessToken ?? ""}` },
                         body: JSON.stringify(patch),
                       });
                       if (!rsp.ok) throw new Error(`HTTP ${rsp.status} — ${await rsp.text()}`);
                       setEditing(null);
                       await studies.refetch();
                     } catch (e) { setErr((e as Error).message); }
                     finally { setBusy(null); }
                   }} />
      </Show>
      <Show when={deleting()}>
        <DeleteModal study={deleting()!}
                     busy={busy() === "delete"}
                     onClose={() => setDeleting(null)}
                     onConfirm={async (reason) => {
                       setBusy("delete"); setErr(null);
                       try {
                         const auth = JSON.parse(sessionStorage.getItem("vpd.auth.v1") ?? "null");
                         // POST body with `reason` (the canonical
                         // shape `studyAdmin.softDelete` expects).
                         // The server sets purge_scheduled_at to
                         // deleted_at + 30 days so the Recycle-bin
                         // sweep can later hard-delete the row.
                         const rsp = await fetch(`/viewer-api/dicom-rs/studies/${encodeURIComponent(deleting()!.studyUid)}`, {
                           method: "DELETE",
                           headers: { "content-type": "application/json", Authorization: `Bearer ${auth?.accessToken ?? ""}` },
                           body: JSON.stringify({ reason }),
                         });
                         if (!rsp.ok) throw new Error(`HTTP ${rsp.status} — ${await rsp.text()}`);
                         setDeleting(null);
                         await studies.refetch();
                       } catch (e) { setErr((e as Error).message); }
                       finally { setBusy(null); }
                     }} />
      </Show>
    </div>
  );
}

function EditModal(p: {
  study: Study;
  busy:  boolean;
  onClose: () => void;
  onSave:  (patch: { mrn?: string; accession?: string; modality?: string; description?: string; study_date?: string }) => void | Promise<void>;
}) {
  const [mrn,         setMrn]         = createSignal(p.study.patientId  ?? "");
  const [accession,   setAccession]   = createSignal(p.study.accession   ?? "");
  const [modality,    setModality]    = createSignal(p.study.modality    ?? "");
  const [description, setDescription] = createSignal(p.study.description ?? "");
  const [studyDate,   setStudyDate]   = createSignal(p.study.studyDate   ?? "");
  return (
    <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
         onClick={p.onClose}>
      <div class="w-[440px] rounded-md border border-ink-300 bg-ink-100 p-4 text-[11px]"
           onClick={(e) => e.stopPropagation()}>
        <h3 class="text-sm font-semibold text-white">Edit study metadata</h3>
        <p class="mt-0.5 text-[10px] text-ink-700">
          Each save writes a <code>study.update</code> audit row with before/after.
        </p>
        <div class="mt-3 flex flex-col gap-2">
          <Field label="MRN"        value={mrn()}         onInput={setMrn} />
          <Field label="Accession"  value={accession()}   onInput={setAccession} />
          <Field label="Modality"   value={modality()}    onInput={setModality} />
          <Field label="Study date (YYYYMMDD)" value={studyDate()}  onInput={setStudyDate} />
          <Field label="Description" value={description()} onInput={setDescription} />
        </div>
        <div class="mt-4 flex justify-end gap-2">
          <button class="btn-ghost text-[11px]" type="button" onClick={p.onClose}>Cancel</button>
          <button class="btn-primary text-[11px]" type="button" disabled={p.busy}
                  onClick={() => p.onSave({
                    mrn: mrn(), accession: accession(), modality: modality(),
                    description: description(), study_date: studyDate(),
                  })}>
            {p.busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function DeleteModal(p: {
  study: Study;
  busy:  boolean;
  onClose:   () => void;
  onConfirm: (reason: string) => void | Promise<void>;
}) {
  const [reason, setReason] = createSignal("");
  return (
    <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
         onClick={p.onClose}>
      <div class="w-[420px] rounded-md border border-danger/40 bg-ink-100 p-4 text-[11px]"
           onClick={(e) => e.stopPropagation()}>
        <h3 class="text-sm font-semibold text-danger">Soft-delete study</h3>
        <p class="mt-0.5 text-[10px] text-ink-700">
          The study will be hidden from every list (Worklist, Studies, Endoscopy)
          but the underlying objects + audit chain are preserved.  After 30 days
          in Recycle bin the purge sweep hard-deletes it; until then the row
          can be restored from <b>Admin → Recycle bin</b>.  Hard purge is a
          separate owner-only flow.
        </p>
        <div class="mt-3 rounded border border-ink-300 bg-ink-200 p-2 text-[11px] text-ink-900">
          <div><b>Patient:</b> {p.study.patientName} ({p.study.patientId})</div>
          <div><b>Accession:</b> {p.study.accession || "—"}</div>
          <div><b>Description:</b> {p.study.description || "—"}</div>
        </div>
        <div class="mt-3">
          <Field label="Reason (audit-logged, ≥10 chars)"
                 value={reason()} onInput={setReason} />
          <Show when={reason().trim().length > 0 && reason().trim().length < 10}>
            <p class="mt-1 text-[10px] text-warn">
              {`${reason().trim().length} / 10 chars — keep going. (CAP COM.40400 / 21 CFR Part 11 §11.10(e))`}
            </p>
          </Show>
        </div>
        <div class="mt-4 flex justify-end gap-2">
          <button class="btn-ghost text-[11px]" type="button" onClick={p.onClose}>Cancel</button>
          <button class="rounded border border-danger bg-danger px-3 py-1 text-[11px] text-white hover:bg-danger/80 disabled:opacity-40"
                  type="button" disabled={p.busy || reason().trim().length < 10}
                  onClick={() => p.onConfirm(reason().trim())}>
            {p.busy ? "Deleting…" : "Soft-delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field(p: { label: string; value: string; onInput: (v: string) => void }) {
  return (
    <label class="flex flex-col gap-1 text-[10px] uppercase tracking-wider text-ink-700">
      {p.label}
      <input type="text" value={p.value}
             class="rounded border border-ink-300 bg-ink-200 px-2 py-1 text-[12px] text-ink-900 normal-case tracking-normal focus:border-brand focus:outline-none"
             onInput={(e) => p.onInput(e.currentTarget.value)} />
    </label>
  );
}

/**
 * DataTable column spec for the Studies page. Declared as a factory
 * so the closures over `canMutate`, `countsFor`, and the edit /
 * delete setters stay correct without triggering a re-render of
 * DataTable's internal prefs state on every render. */
function studyColumns(
  canMutate: () => boolean,
  countsFor: (uid: string) => { video_count: number; image_count: number },
  sourceFor: (uid: string) => string,
  setEditing:  (s: Study | null) => void,
  setDeleting: (s: Study | null) => void,
): DataColumn<Study>[] {
  return [
    {
      id: "case", label: "Case ID",
      render: (s) => <span class="font-mono text-ink-900 group-hover:text-brand" title={s.studyUid}>{caseId(s.studyUid)}</span>,
      sortValue: (s) => caseId(s.studyUid),
      alwaysVisible: true,
    },
    {
      id: "patient", label: "Patient",
      render: (s) => (
        <span class="font-medium text-white group-hover:text-brand">
          {s.patientName ?? "—"}
        </span>
      ),
      sortValue: (s) => (s.patientName ?? "").toLowerCase(),
      alwaysVisible: true,
    },
    {
      id: "mrn", label: "MRN",
      render: (s) => <span class="font-mono text-ink-700">{s.patientId}</span>,
      sortValue: (s) => (s.patientId ?? "").toLowerCase(),
    },
    {
      id: "source", label: "EndoEdge box",
      render: (s) => <span class="text-ink-700">{sourceFor(s.studyUid)}</span>,
      sortValue: (s) => sourceFor(s.studyUid),
      alwaysVisible: true,
    },
    {
      id: "accession", label: "Accession",
      render: (s) => <span>{s.accession || "—"}</span>,
      sortValue: (s) => (s.accession ?? "").toLowerCase(),
    },
    {
      id: "date", label: "Date",
      render: (s) => <span>{fmtDate(s.studyDate)}</span>,
      sortValue: (s) => s.studyDate ?? "",
    },
    {
      id: "modality", label: "Modality",
      render: (s) => (
        <span class="chip-brand" title={modalityTitle(s.modality)}
              aria-label={modalityName(s.modality) || s.modality}>
          {s.modality}
        </span>
      ),
      sortValue: (s) => s.modality ?? "",
    },
    {
      id: "videos", label: "Videos",
      render: (s) => {
        const c = countsFor(s.studyUid).video_count;
        return <span class={c ? "font-mono text-ink-900" : "font-mono text-ink-600"}>{c}</span>;
      },
      sortValue: (s) => countsFor(s.studyUid).video_count,
      cellClass: "text-right",
      headerClass: "text-right",
    },
    {
      id: "images", label: "Images",
      render: (s) => {
        const c = countsFor(s.studyUid).image_count;
        return <span class={c ? "font-mono text-ink-900" : "font-mono text-ink-600"}>{c}</span>;
      },
      sortValue: (s) => countsFor(s.studyUid).image_count,
      cellClass: "text-right",
      headerClass: "text-right",
    },
    {
      id: "instances", label: "Instances",
      render: (s) => <span class="font-mono">{s.instanceCount}</span>,
      sortValue: (s) => s.instanceCount,
      cellClass: "text-right",
      headerClass: "text-right",
      defaultVisible: false,    // Videos + Images split is now the default
    },
    {
      id: "description", label: "Description",
      render: (s) => <span class="truncate text-ink-700">{s.description ?? "—"}</span>,
      sortValue: (s) => (s.description ?? "").toLowerCase(),
    },
    {
      id: "actions", label: "Actions",
      render: (s) => (
        <Show when={canMutate()}>
          <div class="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
            <button type="button"
                    class="rounded border border-ink-300 px-2 py-0.5 text-[10px] text-ink-700 hover:border-brand hover:text-white"
                    title="Edit study metadata"
                    onClick={() => setEditing(s)}>
              Edit
            </button>
            <button type="button"
                    class="rounded border border-danger px-2 py-0.5 text-[10px] text-danger hover:bg-danger hover:text-white"
                    title="Soft-delete this study (audit-logged)"
                    onClick={() => setDeleting(s)}>
              Delete
            </button>
          </div>
        </Show>
      ),
      cellClass:   "text-right",
      headerClass: "text-right",
    },
  ];
}

function fmtDate(d: string): string {
  if (!d || d.length !== 8) return d;
  return `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6, 8)}`;
}
